import { Button } from "common/components/Button";
import { P } from "common/components/Paragraph";
import React from "react";
import toast, { Toast } from "react-hot-toast";

export type FlashLevelTypes = "success" | "info" | "warning" | "danger" | "update";

export const SUCCESS: Exclude<FlashLevelTypes, "update"> = "success";
export const INFO: Exclude<FlashLevelTypes, "update"> = "info";
export const WARNING: Exclude<FlashLevelTypes, "update"> = "warning";
export const DANGER: Exclude<FlashLevelTypes, "update"> = "danger";

export interface FlashAlertAction {
  text: string;
  onClick: () => void;
}

export interface FlashAlertOptions {
  action?: FlashAlertAction;
  onClose?: () => void;
  /** Must be unique per live alert. */
  id?: string;
  /** Heading shown in place of the level's own, for an alert whose level is only its severity. */
  title?: string;
}

export interface FlashAlertProps {
  message: string;
  level: Exclude<FlashLevelTypes, "update">;
  toastData: Toast;
  action?: FlashAlertAction;
  onClose?: () => void;
  title?: string;
}

export interface UpdateFlashAlertProps {
  level: "update";
  toastData: Toast;
  message?: never;
  action?: never;
  onClose?: never;
  title?: never;
}

export type CombinedFlashAlertProps = FlashAlertProps | UpdateFlashAlertProps;

/**
 * Should not be used directly. Use `addFlashAlert` instead to dispatch a notification alert.
 */
export const FlashAlert: React.FC<CombinedFlashAlertProps> = ({
  message,
  level,
  toastData,
  action,
  onClose,
  title,
}) => {
  const updateMessage = "A new version of the app is available. Refresh the page to update.";
  const backgrounds = {
    success: "bg-success-50",
    info: "bg-blue-50",
    warning: "bg-warning-50",
    danger: "bg-error-50",
    update: "bg-secondary-50",
  };
  const borders = {
    success: "border-success-400",
    info: "border-blue-400",
    warning: "border-warning-100",
    danger: "border-error-400",
    update: "",
  };
  const icons = {
    success: "verified_user",
    info: "info",
    warning: "warning",
    danger: "close",
    update: "info",
  };
  const iconBackgrounds = {
    success: "bg-[#27C069]",
    info: "bg-blue-600",
    warning: "bg-warning-200",
    danger: "bg-error-400",
    update: "bg-blue-600",
  };
  const titles = {
    success: "Success!",
    info: "Info!",
    warning: "Warning!",
    danger: "Something went wrong!",
    update: "Update Available",
  };

  const close = () => {
    onClose?.();
    toast.dismiss(toastData.id);
  };

  const update = async () => {
    const registrations = await navigator.serviceWorker.getRegistrations();
    let waitingDefined = false;
    registrations.forEach((reg) => {
      if (reg.waiting) {
        waitingDefined = true;
        reg.waiting?.postMessage({ type: "TRIGGER_SKIP_WAITING" });
      }
    });
    if (!waitingDefined) {
      console.error("No waiting service worker found");
      window.location.reload();
    }
    close();
  };

  return (
    <div
      id={`flash-container-${toastData.id}`}
      className={`p-md w-[400px] border shadow-lg rounded pointer-events-auto ring-1 ring-black ring-opacity-5 ${
        backgrounds[level]
      } ${borders[level]} ${toastData.visible ? "animate-slide-in" : "animate-slide-out"}`}>
      <div className="flex items-center">
        <span
          className={`material-symbols-outlined font-light mr-xs text-[30px] text-white rounded-full ml-1 p-[6px] ${iconBackgrounds[level]}`}>
          {icons[level]}
        </span>

        <div className="mx-2 text-secondary-900">
          <h5 className="mb-xs">{title || titles[level]}</h5>
          <P size="md">{message || updateMessage}</P>
        </div>
        <button aria-label="Close" className="flex flex-col self-stretch ml-auto justify-start" onClick={close}>
          <span className="material-symbols-outlined flex text-secondary-900 text-[22px]">close</span>
        </button>
      </div>
      {action && (
        <div className="flex w-full justify-end mt-2">
          <Button
            analyticsAction={`flash alert ${action.text.toLowerCase()}`}
            onClick={() => {
              action.onClick();
              toast.dismiss(toastData.id);
            }}>
            {action.text}
          </Button>
        </div>
      )}
      {level === "update" && (
        <div className="flex w-full justify-end mt-2">
          <Button analyticsAction="update later" variant="secondary" onClick={close} className="mr-2">
            Later
          </Button>
          <Button analyticsAction="update now" onClick={update}>
            Update
          </Button>
        </div>
      )}
    </div>
  );
};

export function addFlashAlert(level: Exclude<FlashLevelTypes, "update">, message: string): void;
export function addFlashAlert(level: "update"): void;
export function addFlashAlert(
  level: FlashLevelTypes,
  message?: string,
  persist?: boolean,
  options?: FlashAlertOptions,
): void;
export function addFlashAlert(
  level: FlashLevelTypes,
  message?: string,
  persist?: boolean,
  options: FlashAlertOptions = {},
): void {
  if (level === "update") {
    toast.custom((t: Toast) => <FlashAlert level={level} toastData={t} />, { duration: Infinity });
  } else {
    toast.custom(
      (t: Toast) => (
        <FlashAlert
          message={message!}
          level={level}
          toastData={t}
          action={options.action}
          onClose={options.onClose}
          title={options.title}
        />
      ),
      { duration: persist ? Infinity : undefined, ...(options.id ? { id: options.id } : {}) },
    );
  }
}

export const removeFlashAlertByExternalId = (id: string): void => {
  toast.dismiss(id);
};
